import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { copyFlowSurvey, type FlowSurveyDetail } from '../../services/flowSurveyService';

export interface CopySurveyFormProps {
  survey: FlowSurveyDetail;
  /** Prefilled time of the new measurement, e.g. the start of the night mode. */
  clock: string | null;
  onCreated: (survey: FlowSurveyDetail) => void;
  onCancel: () => void;
}

function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Measure again at another time (e.g. at night): a linked copy of the survey
 * with its pumps, readings and corals — then only the new ranges (and the pumps'
 * programme at that time) need entering.
 */
export function CopySurveyForm({ survey, clock, onCreated, onCancel }: CopySurveyFormProps) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(String(t('flow.copy.defaultTitle', { title: survey.title })).slice(0, 120));
  const [date, setDate] = useState(today());
  const [time, setTime] = useState(clock ?? '22:00');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const copy = useMutation({
    mutationFn: () => copyFlowSurvey(survey.aquariumId, survey.id, { title: title.trim(), measured_at: new Date(`${date}T12:00:00`).toISOString(), measured_clock: time || null }),
    onSuccess: onCreated,
  });
  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (title.trim() === '') next.title = String(t('flow.new.titleRequired'));
    if (!date) next.date = String(t('flow.new.dateRequired'));
    setErrors(next);
    if (Object.keys(next).length === 0) copy.mutate();
  };
  return (
    <form onSubmit={submit} noValidate>
      <Stack gap="4">
        <Text color="secondary" size="sm">{t('flow.copy.intro')}</Text>
        <Input id="flow-copy-title" label={t('flow.new.title')} value={title} onChange={(e) => setTitle(e.target.value)} error={errors.title} maxLength={120} fullWidth />
        <Inline gap="3" align="start">
          <Input id="flow-copy-date" type="date" label={t('flow.new.date')} value={date} onChange={(e) => setDate(e.target.value)} error={errors.date} />
          <Input id="flow-copy-time" type="time" label={t('flow.new.time')} value={time} onChange={(e) => setTime(e.target.value)} />
        </Inline>
        {copy.isError && <Alert>{t('flow.copy.error')}</Alert>}
        <Inline gap="2" justify="end" wrap>
          <Button type="button" variant="ghost" onClick={onCancel}>{t('flow.cancel')}</Button>
          <Button type="submit" isLoading={copy.isPending}>{t('flow.copy.submit')}</Button>
        </Inline>
      </Stack>
    </form>
  );
}

export default CopySurveyForm;
